import React from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useUserStore } from "@/stores/userStore";

export interface EditProfileFormProps {
  user: {
    name: string | null;
    gender: string | null;
    birthday: string | null;
    bio: string | null;
    location: string | null;
    avatarUrl: string | null;
  };
  className?: string;
  onCancel?: () => void;
  onSaved?: (updated: any) => void;
}

const EditProfileForm: React.FC<EditProfileFormProps> = ({
  user,
  className,
  onCancel,
  onSaved,
}) => {
  const { t } = useTranslation();
  const updateUser = useUserStore((state) => state.updateUser);
  const isLoading = useUserStore((state) => state.isLoading);

  // 性别选项映射
  const genderOptions = [
    { value: "male", label: t("profile.form.gender.male") },
    { value: "female", label: t("profile.form.gender.female") },
    { value: "secret", label: t("profile.form.gender.secret") },
  ];

  // 将用户性别值映射到内部值
  const mapGenderToValue = (gender: string | null): string => {
    if (gender === "男" || gender === "male") return "male";
    if (gender === "女" || gender === "female") return "female";
    return "secret";
  };

  // 将内部值映射回服务器值
  const mapValueToGender = (value: string): string => {
    if (value === "male") return "男";
    if (value === "female") return "女";
    return "保密";
  };

  const [preview, setPreview] = React.useState<string | null>(user.avatarUrl);
  const [birthday, setBirthday] = React.useState<Date | undefined>(
    user.birthday ? new Date(user.birthday) : undefined
  );
  const [showCal, setShowCal] = React.useState(false);
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [gender, setGender] = React.useState<string>(
    mapGenderToValue(user.gender)
  );

  const formatDate = (date?: Date) =>
    date ? date.toISOString().slice(0, 10) : "";

  // 处理头像预览
  const handleAvatarChange: React.ChangeEventHandler<HTMLInputElement> = (
    e
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const csrfToken =
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.content || "";

  return (
    <form
      encType="multipart/form-data"
      className={cn("space-y-6", className)}
      onSubmit={async (e) => {
        e.preventDefault();
        const form = e.currentTarget as HTMLFormElement;
        const formData = new FormData(form);

        try {
          const nameVal = formData.get("user[name]") as string;
          if (!nameVal || nameVal.length < 2 || nameVal.length > 30) {
            setNameError(t("profile.form.name.error"));
            return;
          }

          const file =
            (formData.get("user[avatar]") as File).size > 0
              ? (formData.get("user[avatar]") as File)
              : undefined;

          await updateUser({
            name: nameVal,
            bio: formData.get("user[bio]") as string,
            gender: mapValueToGender(gender),
            birthday: formatDate(birthday),
            location: formData.get("user[location]") as string,
            avatar: file,
          });

          toast.success(t("profile.form.saveSuccess"));
          onSaved?.({});
        } catch (error) {
          console.error("Failed to update profile", error);
        }
      }}
      noValidate
    >
      {/* Rails method override & CSRF */}
      <input type="hidden" name="_method" value="patch" />
      {csrfToken && (
        <input type="hidden" name="authenticity_token" value={csrfToken} />
      )}

      {/* 头像上传 */}
      <div
        className="bg-card rounded-2xl border border-border p-6"
        data-controller="avatar-upload"
      >
        <div className="flex items-center space-x-6">
          <div className="flex-shrink-0">
            <div className="relative group">
              {preview ? (
                <img
                  src={preview}
                  alt="avatar"
                  className="h-16 w-16 rounded-2xl object-cover"
                />
              ) : (
                <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center">
                  <span className="text-2xl text-muted-foreground">
                    {user.name?.[0]}
                  </span>
                </div>
              )}
              <label
                htmlFor="user_avatar"
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              >
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </label>
              <input
                type="file"
                id="user_avatar"
                name="user[avatar]"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">
              {t("profile.form.avatar.title")}
            </h3>
            <p className="text-xs mt-2 text-muted-foreground">
              {t("profile.form.avatar.hint")}
            </p>
          </div>
        </div>
      </div>

      {/* 基本信息 */}
      <div className="bg-card rounded-2xl border border-border p-6 space-y-6">
        <h3 className="text-base font-semibold text-foreground">
          {t("profile.form.basicInfo")}
        </h3>

        {/* 昵称 */}
        <div className="md:col-span-1 my-8 space-y-1">
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <Label
              htmlFor="user_name"
              className="md:w-24 text-left md:text-right shrink-0"
            >
              {t("profile.form.name.label")}
            </Label>
            <TooltipProvider delayDuration={100}>
              <Tooltip open={!!nameError}>
                <TooltipTrigger asChild>
                  <Input
                    id="user_name"
                    name="user[name]"
                    defaultValue={user.name || ""}
                    placeholder={t("profile.form.name.placeholder")}
                    minLength={2}
                    maxLength={30}
                    required
                    className="w-full md:max-w-xs"
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      if (val.length < 2 || val.length > 30) {
                        setNameError(t("profile.form.name.error"));
                      } else {
                        setNameError(null);
                      }
                    }}
                    onChange={() => {
                      if (nameError) setNameError(null);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent side="right">{nameError}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-xs mt-3 text-muted-foreground md:ml-[6.6rem]">
            {t("profile.form.name.hint")}
          </p>
        </div>

        {/* 性别 */}
        <div className="space-y-2 my-8 md:col-span-1">
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <Label className="md:w-24 text-left md:text-right shrink-0">
              {t("profile.form.gender.label")}
            </Label>
            <div className="w-full md:max-w-xs">
              <Select value={gender} onValueChange={(val) => setGender(val)}>
                <SelectTrigger
                  className="w-full"
                  aria-label={t("profile.form.gender.label")}
                >
                  <SelectValue
                    placeholder={t("profile.form.gender.placeholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {genderOptions.map((g) => (
                    <SelectItem key={g.value} value={g.value}>
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input
                type="hidden"
                name="user[gender]"
                value={mapValueToGender(gender)}
              />
            </div>
          </div>
        </div>

        {/* 生日 */}
        <div className="space-y-1 my-8 relative md:col-span-1">
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <Label className="md:w-24 text-left md:text-right shrink-0">
              {t("profile.form.birthday.label")}
            </Label>
            <button
              type="button"
              className="relative w-full md:max-w-xs h-10 pl-3 pr-9 py-2 border border-input rounded-md text-left text-sm"
              onClick={() => setShowCal((o) => !o)}
            >
              {birthday
                ? formatDate(birthday)
                : t("profile.form.birthday.placeholder")}
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            </button>
          </div>
          {/* Hidden input for Rails */}
          <input
            type="hidden"
            name="user[birthday]"
            value={formatDate(birthday)}
          />

          {showCal && (
            <div className="absolute z-50 mt-2 bg-card rounded-xl shadow-lg border border-border p-2">
              <Calendar
                mode="single"
                captionLayout="dropdown"
                fromYear={1900}
                toYear={new Date().getFullYear()}
                selected={birthday}
                onSelect={(date: Date | undefined) => {
                  if (date) setBirthday(date);
                  setShowCal(false);
                }}
                initialFocus
              />
            </div>
          )}
          <p className="text-xs mt-3 text-muted-foreground md:ml-[6.6rem]">
            {t("profile.form.birthday.hint")}
          </p>
        </div>

        {/* 简介 */}
        <div className="space-y-1 my-8 md:col-span-2">
          <div className="flex flex-col md:flex-row gap-2">
            <Label
              htmlFor="user_bio"
              className="md:w-24 text-left md:text-right shrink-0 pt-2"
            >
              {t("profile.form.bio.label")}
            </Label>
            <Textarea
              id="user_bio"
              name="user[bio]"
              rows={3}
              defaultValue={user.bio || ""}
              placeholder={t("profile.form.bio.placeholder")}
              className="w-full md:max-w-xs"
            />
          </div>
          <p className="text-xs mt-3 text-muted-foreground md:ml-[6.6rem]">
            {t("profile.form.bio.hint")}
          </p>
        </div>

        {/* 所在城市 */}
        <div className="space-y-1 my-4  md:col-span-1">
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <Label
              htmlFor="user_location"
              className="md:w-24 text-left md:text-right shrink-0"
            >
              {t("profile.form.location.label")}
            </Label>
            <Input
              id="user_location"
              name="user[location]"
              defaultValue={user.location || ""}
              placeholder={t("profile.form.location.placeholder")}
              maxLength={30}
              className="w-full md:max-w-xs"
            />
          </div>
          <p className="text-xs mt-3 text-muted-foreground md:ml-[6.6rem]">
            {t("profile.form.location.hint")}
          </p>
        </div>
      </div>

      {/* 操作按钮 */}
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-4 pt-2">
        <Button
          variant="outline"
          size="default"
          className="w-full sm:w-32"
          type="button"
          onClick={onCancel}
        >
          {t("profile.form.cancel")}
        </Button>
        <Button
          type="submit"
          size="default"
          className="w-full sm:w-32"
          disabled={isLoading}
        >
          {t("profile.form.submit")}
        </Button>
      </div>
    </form>
  );
};

export default EditProfileForm;
